import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { getOrderMutationErrorMessage } from "@/features/order/errors";
import {
  useLegacyKgMigrationPreview,
  useMigrateLegacyKgItem,
} from "@/features/order/hooks";
import type { LegacyKgMigrationPayload } from "@/features/order/types";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { formatMoneyInput, parseMoneyInput } from "@/lib/timeAgo";
import {
  getWholeDirectKgNonNegativeQuantityInputAttributes,
  getWholeDirectKgQuantityInputAttributes,
  validateWholeDirectKgNonNegativeQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";

type LegacyKgMigrationPanelProps = {
  orderId: string;
  itemId: string;
  onClose: () => void;
  onSuccess: () => void;
};

function toInputValue(value: number) {
  return Number.isFinite(value) ? String(value) : "";
}

function requiredWholeValue(
  rawValue: string,
  label: string,
): { value: number } | { error: string } {
  const validation = validateWholeDirectKgNonNegativeQuantityInput(rawValue);
  if (validation.quantity == null) {
    return {
      error: `${label} deve ser um número inteiro igual ou maior que zero.`,
    };
  }
  return { value: validation.quantity };
}

export function LegacyKgMigrationPanel({
  orderId,
  itemId,
  onClose,
  onSuccess,
}: LegacyKgMigrationPanelProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const [quantity, setQuantity] = useState("");
  const [bonusQuantity, setBonusQuantity] = useState("");
  const [finalPrice, setFinalPrice] = useState("");
  const [receivedQuantity, setReceivedQuantity] = useState("");
  const [returnLotQuantities, setReturnLotQuantities] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  const preview = useLegacyKgMigrationPreview(orderId, itemId);
  const migrate = useMigrateLegacyKgItem();

  useDialogFocus(dialogRef, () => requestClose(), {
    initialFocusRef: quantityRef,
  });

  useEffect(() => {
    if (!preview.data || initializedRef.current) return;

    initializedRef.current = true;
    setQuantity(toInputValue(preview.data.quantity));
    setBonusQuantity(
      preview.data.bonus_quantity === 0
        ? ""
        : toInputValue(preview.data.bonus_quantity),
    );
  }, [preview.data]);

  function requestClose() {
    if (!migrate.isPending) onClose();
  }

  function updateReturnLotQuantity(returnLotId: string, value: string) {
    setReturnLotQuantities((current) => ({
      ...current,
      [returnLotId]: value,
    }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const validatedQuantity = validateWholeDirectKgQuantityInput(quantity);
    if (validatedQuantity.quantity == null) {
      setError(
        "A quantidade migrada deve ser um número inteiro maior que zero.",
      );
      return;
    }

    const payload: LegacyKgMigrationPayload = {
      quantity: validatedQuantity.quantity,
    };

    if (bonusQuantity.trim()) {
      const validatedBonus = requiredWholeValue(bonusQuantity, "A bonificação");
      if ("error" in validatedBonus) {
        setError(validatedBonus.error);
        return;
      }
      payload.bonus_quantity = validatedBonus.value;
    }

    if (finalPrice.trim()) {
      const parsedFinalPrice = parseMoneyInput(finalPrice);
      if (parsedFinalPrice == null || parsedFinalPrice < 0) {
        setError(
          "O preço final deve ser um valor válido igual ou maior que zero.",
        );
        return;
      }
      payload.final_price = parsedFinalPrice;
    }

    const checkin = preview.data?.checkin;
    if (checkin) {
      const reconciliation: NonNullable<
        LegacyKgMigrationPayload["reconciliation"]
      > = {};

      if (checkin.requires_received_quantity_reconciliation) {
        const validatedReceived = requiredWholeValue(
          receivedQuantity,
          "A quantidade recebida",
        );
        if ("error" in validatedReceived) {
          setError(validatedReceived.error);
          return;
        }
        reconciliation.received_quantity = validatedReceived.value;
      }

      const requiredReturnLots = checkin.return_lots.filter(
        (returnLot) => returnLot.requires_reconciliation,
      );
      if (requiredReturnLots.length > 0) {
        reconciliation.return_lots = [];
        for (const returnLot of requiredReturnLots) {
          const validatedReturnLot = requiredWholeValue(
            returnLotQuantities[returnLot.return_lot_id] ?? "",
            "A quantidade devolvida",
          );
          if ("error" in validatedReturnLot) {
            setError(validatedReturnLot.error);
            return;
          }
          reconciliation.return_lots.push({
            return_lot_id: returnLot.return_lot_id,
            quantity: validatedReturnLot.value,
          });
        }
      }

      if (
        reconciliation.received_quantity !== undefined ||
        reconciliation.return_lots?.length
      ) {
        payload.reconciliation = reconciliation;
      }
    }

    try {
      await migrate.mutateAsync({ orderId, itemId, payload });
      onSuccess();
      onClose();
    } catch (caughtError) {
      setError(getOrderMutationErrorMessage(caughtError));
    }
  }

  return createPortal(
    <div className="legacy-kg-migration-overlay" onClick={requestClose}>
      <div
        ref={dialogRef}
        className="legacy-kg-migration-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Migrar item histórico de kg"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="legacy-kg-migration-header">
          <div>
            <h2>Migrar item histórico de kg</h2>
            <p>
              Informe valores inteiros. Nenhuma quantidade será arredondada.
            </p>
          </div>
          <button
            type="button"
            className="legacy-kg-migration-close"
            aria-label="Fechar"
            onClick={requestClose}
            disabled={migrate.isPending}
          >
            <X size={19} />
          </button>
        </header>

        {preview.isLoading && (
          <p className="legacy-kg-migration-state" role="status">
            Carregando prévia da migração...
          </p>
        )}
        {preview.isError && (
          <p className="legacy-kg-migration-state is-error" role="alert">
            {getOrderMutationErrorMessage(preview.error)}
          </p>
        )}
        {preview.data && (
          <form
            className="legacy-kg-migration-form"
            noValidate
            onSubmit={handleSubmit}
          >
            <label>
              Quantidade migrada (kg)
              <input
                ref={quantityRef}
                aria-label="Quantidade migrada (kg)"
                type="number"
                {...getWholeDirectKgQuantityInputAttributes()}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </label>
            <label>
              Bonificação (kg, opcional)
              <input
                aria-label="Bonificação (kg, opcional)"
                type="number"
                {...getWholeDirectKgNonNegativeQuantityInputAttributes()}
                value={bonusQuantity}
                onChange={(event) => setBonusQuantity(event.target.value)}
              />
            </label>
            <label>
              Preço final (R$, opcional)
              <input
                aria-label="Preço final (R$, opcional)"
                type="text"
                inputMode="decimal"
                value={finalPrice}
                onChange={(event) =>
                  setFinalPrice(formatMoneyInput(event.target.value))
                }
              />
            </label>

            {preview.data.checkin
              ?.requires_received_quantity_reconciliation && (
              <label>
                Quantidade recebida para conferência (kg)
                <input
                  aria-label="Quantidade recebida para conferência (kg)"
                  type="number"
                  {...getWholeDirectKgNonNegativeQuantityInputAttributes()}
                  value={receivedQuantity}
                  onChange={(event) => setReceivedQuantity(event.target.value)}
                />
              </label>
            )}

            {preview.data.checkin?.return_lots
              .filter((returnLot) => returnLot.requires_reconciliation)
              .map((returnLot) => (
                <label key={returnLot.return_lot_id}>
                  Quantidade devolvida para conferência (kg)
                  <span className="legacy-kg-migration-note">
                    Lote {returnLot.return_lot_id} · atual: {returnLot.quantity}{" "}
                    kg
                  </span>
                  <input
                    aria-label={`Quantidade devolvida para conferência (kg) ${returnLot.return_lot_id}`}
                    type="number"
                    {...getWholeDirectKgNonNegativeQuantityInputAttributes()}
                    value={returnLotQuantities[returnLot.return_lot_id] ?? ""}
                    onChange={(event) =>
                      updateReturnLotQuantity(
                        returnLot.return_lot_id,
                        event.target.value,
                      )
                    }
                  />
                </label>
              ))}

            {error && (
              <p className="legacy-kg-migration-error" role="alert">
                {error}
              </p>
            )}

            <footer className="legacy-kg-migration-actions">
              <button
                type="button"
                onClick={requestClose}
                disabled={migrate.isPending}
              >
                Cancelar
              </button>
              <button type="submit" disabled={migrate.isPending}>
                {migrate.isPending ? (
                  <LoaderCircle className="order-action-spinner" size={16} />
                ) : null}
                Migrar item histórico
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
